import React, { useEffect, useRef, useState } from 'react';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import './MiniChartWidget.css';
import { getFlagSvg } from '../../Components/Common/Flag/Flag';
import { MarketIndicesType, StockData } from '../../types/indices.type';
import classNames from 'classnames';
import { formatNumber } from '../../utils/utils';

import {
  getMinichartPeerCompare,
  getMinichartSearch,
} from '../../api/company.api';
import SvgImage from '../../Components/Common/SvgImage/SvgImage';
import {
  BaseWidgetProps,
  CommonTooltipType,
  WidgetHeightMessage,
} from '../../types/common.type';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import {
  Region,
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';

import { apiBase } from '../../api/apiBase';
export interface MiniChartWidgetProps extends BaseWidgetProps {
  region?: Region | string;
  rics?: string | string[];
  token?: string;
  showPeers?: boolean;
  peerCount?: number;
  onCallback?: (message: StockData) => void;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const MiniChartWidget = (props: MiniChartWidgetProps) => {
  const { onHeightChange } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(containerRef, WidgetNameSource.MiniChart, onHeightChange);

  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'indexList');
  const [displayedData, setDisplayedData] = useState<MarketIndicesType[]>([]);

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });

  useEffect(() => {
    const handleScroll = () => {
      setTooltip((prev) => ({ ...prev, show: false }));
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  useEffect(() => {
    if (!tooltip.show) return;
    const handleClickOutside = () => {
      setTooltip((prev) => ({ ...prev, show: false }));
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [tooltip.show]);

  const handleNameClick = (
    e: React.MouseEvent<HTMLDivElement>,
    name: string,
  ) => {
    e.stopPropagation();
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const x = rect.left + scrollX;
    const y = rect.bottom + 2 + scrollY;
    setTooltip({
      content: name,
      show: true,
      x,
      y,
    });
  };

  const getMiniChartUrl = (ric: string) => {
    return `${apiBase()}/chart/?int=2&sym=${ric}&style=1&per=1&token=${encodeURIComponent(props.token || '')}`;
  };

  useEffect(() => {
    setIsLoading(true);
    setIsEmptyData(false);
    setDisplayedData([]);

    let apiRegion = props.region || Region.All;

    let ricsList: string[] = [];
    if (props.rics) {
      ricsList = Array.isArray(props.rics)
        ? props.rics
        : props.rics.split(/[;,]/);
    }

    // Trim rics string (or array elements) to remove spaces
    let cleanRics = ricsList
      .map((ric) => ric.trim())
      .filter((ric) => ric !== '');
    const hasRics = cleanRics.length > 0;
    const isSectorPeers = props.showPeers ?? false;

    let mappedRegion = apiRegion as Region;
    if (apiRegion === Region.AU) {
      mappedRegion = Region.AU;
    } else if (apiRegion === Region.US) {
      mappedRegion = Region.US;
    } else if (
      apiRegion === Region.All ||
      apiRegion === Region.International ||
      apiRegion === ''
    ) {
      mappedRegion = Region.All;
    }

    if (!hasRics && !isSectorPeers) {
      setIsEmptyData(true);
      setIsLoading(false);
      return;
    }

    const ricsParam = cleanRics.join(';');
    const countParam = Math.min(Math.max(props.peerCount ?? 10, 1), 20);

    const processResponse = (dataList: any[]) => {
      const arr: MarketIndicesType[] = [];
      for (let i = 0; i < dataList.length; i++) {
        const item = dataList[i];
        let haveImg = true;
        let ricKey = item.bell_code || item.ticker || '';
        let ccVal = item.cc || '';
        if (ricKey.endsWith('=RR') || ricKey.endsWith('=XX')) {
          ccVal = 'USA';
          haveImg = false;
        }
        if (ricKey === '.FTSE') {
          ccVal = 'GBP';
        }
        arr.push({
          ric: ricKey,
          chartRic: item.ric || ricKey,
          index: item.ticker || ricKey,
          cc: ccVal,
          name: item.name || '',
          last: item.last !== null && item.last !== undefined ? item.last : '',
          pc:
            item.pctchng !== null && item.pctchng !== undefined
              ? item.pctchng
              : '',
          nc:
            item.netchng !== null && item.netchng !== undefined
              ? item.netchng
              : '',
          haveImg: haveImg,
        });
      }
      setDisplayedData(arr);
      setIsEmptyData(arr.length === 0);
      if (arr.length === 0) {
        props.onNoData?.({
          type: WidgetMessageType.NoData,
          source: 'MiniChartWidget',
          data: null,
        });
      }
    };

    const handleError = (err: any) => {
      console.error('Error fetching mini charts data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    };

    const handleFinally = () => {
      setIsLoading(false);
    };

    if (isSectorPeers) {
      // showPeers = true: validate peerCount range (1-20)
      const rawCount = props.peerCount ?? 10;
      if (rawCount < 1 || rawCount > 20) {
        const err = {
          message: `peerCount must be between 1 and 20, received: ${rawCount}`,
          name: 'ValidationError',
          code: 'INVALID_PEER_COUNT',
        };
        console.error('Error fetching mini charts data:', err.message);
        props.onError?.({
          message: err.message,
          type: err.name + '_' + err.code,
        });
        setIsEmptyData(true);
        setIsLoading(false);
        return;
      }
      getMinichartPeerCompare(
        mappedRegion as string,
        ricsParam,
        isSectorPeers,
        countParam,
        props.token || '',
      )
        .then((res) => processResponse(res?.data || []))
        .catch(handleError)
        .finally(handleFinally);
    } else {
      // showPeers = false: use search/minichart API to get only the specified rics
      getMinichartSearch(mappedRegion as string, ricsParam, props.token || '')
        .then((res) => processResponse(res?.data || []))
        .catch(handleError)
        .finally(handleFinally);
    }
  }, [props.token, props.region, props.rics, props.showPeers, props.peerCount]);

  const handleOnClick = (ric: string, haveImg: boolean) => {
    if (props.onCallback && ric && haveImg) {
      let message = {
        data: { symbol: ric },
        source: 'minicharts',
        type: WidgetMessageType.OnStockClick,
      } as StockData;

      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify(message),
          '*',
        );
      }
      window.parent.postMessage(JSON.stringify(message), '*');
      props.onCallback(message);
    }
  };

  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div ref={containerRef} className="miniChartsWidget">
        {isLoading && !props.onLoadingChange ? (
          <div className="mini-chart-empty-container">
            <Loading />
          </div>
        ) : !isLoading && isEmptyData ? (
          <div className="mini-chart-empty-container">
            <ErrorMessage msg={msgItem} />
          </div>
        ) : (
          <div className="mini-charts-list">
            {displayedData.map((item: MarketIndicesType) => (
              <div key={item.ric} className="mini-chart-item">
                <div className="ticker-block">
                  <div className="flag-icon">{getFlagSvg(item.cc)}</div>
                  <span
                    className={classNames('ticker', {
                      'link-enabled': item.haveImg,
                    })}
                    onClick={() => handleOnClick(item.ric, item.haveImg)}
                  >
                    {item.index}
                  </span>
                </div>

                <div
                  className="company-name"
                  title={item.name}
                  onClick={(e) => handleNameClick(e, item.name)}
                  onMouseEnter={(e) => handleNameClick(e, item.name)}
                  onMouseLeave={() =>
                    setTooltip((prev) => ({ ...prev, show: false }))
                  }
                >
                  {item.name}
                </div>

                <div className="last-price">
                  {item.last
                    ? formatNumber(
                        item.last,
                        item.cc === 'USA' ? 4 : 3,
                        item.cc === 'USA' ? 4 : 3,
                        false,
                        false,
                        false,
                      )
                    : '-'}
                </div>

                <div className="chart-container">
                  {item.haveImg ? (
                    <SvgImage
                      svgApiUrl={getMiniChartUrl(item.chartRic || item.ric)}
                    />
                  ) : (
                    <div className="chart-empty-line"></div>
                  )}
                </div>

                <div
                  className={classNames('pct-chg', {
                    positive: Number(item.nc) > 0,
                    negative: Number(item.nc) < 0,
                    nochange: Number(item.nc) === 0 || !item.nc,
                  })}
                >
                  {item.pc
                    ? formatNumber(item.pc, 2, 2, true, true, false, '%')
                    : '-'}
                </div>
              </div>
            ))}
          </div>
        )}
        <CommonTooltip
          ref={tooltipRef}
          content={tooltip.content}
          show={tooltip.show}
          x={tooltip.x}
          y={tooltip.y}
        />
      </div>
    </Layout>
  );
};

export default MiniChartWidget;
